import { ChartDataPoint, EventData } from 'features/systemLens'
import { Annotations, Shape } from 'plotly.js'

export const getThresholdColor = (type: string) => {
  switch (type) {
    case 'upperUpper':
      return '#dc2626'
    case 'upper':
      return '#f87171'
    case 'lower':
      return '#60a5fa'
    case 'lowerLower':
      return '#1d4ed8'
    default:
      return '#888'
  }
}

export const getThresholdLabel = (type: string) => {
  switch (type) {
    case 'upperUpper':
      return 'Upper High'
    case 'upper':
      return 'Upper'
    case 'lower':
      return 'Lower'
    case 'lowerLower':
      return 'Lower Low'
    default:
      return 'Threshold'
  }
}

/**
 * Generate shapes and annotations for operational events
 */
export function generateEventMarkers(
  events: EventData[],
  enabled: boolean
): {
  shapes: Partial<Shape>[]
  annotations: Partial<Annotations>[]
} {
  const shapes: Partial<Shape>[] = []
  const annotations: Partial<Annotations>[] = []

  if (!enabled || events.length === 0) {
    return { shapes, annotations }
  }

  events.forEach((event) => {
    // Determine line color based on priority and status
    let lineColor = '#60a5fa' // Default blue
    let lineStyle: 'solid' | 'dot' | 'dash' = 'solid'
    let lineWidth = 2

    // Color based on priority
    if (event.metadata?.priority === 'Critical') {
      lineColor = '#ef4444' // Red
      lineWidth = 3
    } else if (event.metadata?.priority === 'High') {
      lineColor = '#f97316' // Orange
      lineWidth = 2.5
    } else if (event.metadata?.priority === 'Medium') {
      lineColor = '#fbbf24' // Yellow
      lineWidth = 2
    } else if (event.metadata?.priority === 'Low') {
      lineColor = '#84cc16' // Green
      lineWidth = 2
    }

    // Line style based on status
    if (event.metadata?.status === 'Scheduled') {
      lineStyle = 'dash' // Dashed for future events
    } else if (event.metadata?.status === 'In Progress') {
      lineStyle = 'dot' // Dotted for ongoing
    } else if (event.metadata?.status === 'Completed') {
      lineStyle = 'solid' // Solid for completed
    }

    // Add vertical line
    shapes.push({
      type: 'line',
      x0: new Date(event.timestamp).toISOString(),
      x1: new Date(event.timestamp).toISOString(),
      xref: 'x',
      y0: 0,
      y1: 1,
      yref: 'paper',
      line: {
        color: lineColor,
        width: lineWidth,
        dash: lineStyle,
      },
    })

    // Add annotation with event details
    const annotationText = event.type

    annotations.push({
      x: new Date(event.timestamp).toISOString(),
      xref: 'x',
      y: 1,
      yref: 'paper',
      text: annotationText,
      showarrow: false,
      font: {
        size: 10,
        color: lineColor,
        family: 'system-ui, -apple-system, sans-serif',
      },
      xanchor: 'center',
      yanchor: 'bottom',
      bgcolor: 'rgba(255, 255, 255, 0.8)',
      bordercolor: lineColor,
      borderwidth: 1,
      borderpad: 2,
    })
  })

  return { shapes, annotations }
}

export function getInterpolatedValue(
  chartData: ChartDataPoint[],
  sensorId: string,
  xTimestamp: number
): number | null {
  let idx = -1
  for (let i = 0; i < chartData.length - 1; i++) {
    if (
      chartData[i].timestamp <= xTimestamp &&
      chartData[i + 1].timestamp >= xTimestamp
    ) {
      idx = i
      break
    }
  }
  // We need at least one point before and one after
  if (idx === -1 || idx >= chartData.length - 1) return null

  // Find nearest non-null value to the left and right
  let leftIdx = idx
  while (
    leftIdx >= 0 &&
    (chartData[leftIdx][sensorId] === null ||
      chartData[leftIdx][sensorId] === undefined)
  ) {
    leftIdx--
  }
  let rightIdx = idx + 1
  while (
    rightIdx < chartData.length &&
    (chartData[rightIdx][sensorId] === null ||
      chartData[rightIdx][sensorId] === undefined)
  ) {
    rightIdx++
  }

  // If we found both left and right indices, we can interpolate using y = mx + b
  if (leftIdx >= 0 && rightIdx < chartData.length) {
    const p1 = chartData[leftIdx]
    const p2 = chartData[rightIdx]
    const y1 = Number(p1[sensorId])
    const y2 = Number(p2[sensorId])
    const t1 = p1.timestamp
    const t2 = p2.timestamp

    if (t2 !== t1) {
      const slope = (y2 - y1) / (t2 - t1)
      return y1 + slope * (xTimestamp - t1)
    }
  }

  return null
}
